import type { QueryClient } from "@tanstack/react-query"
import type { AccessDeniedError } from "@/shared/api/errors"
import { captureError } from "@/shared/lib/diagnostics"
import { confirmedUserWithin } from "../auth/confirmed-at"
import {
	isTransientFailure,
	USER_STALE_MS,
	userQueryOptions,
} from "../auth/user-query"
import type { User } from "./types"

export type UserPermissionKey = keyof User["permissions"]

export type SearchPermissionKey = Extract<
	AccessDeniedError["permission"],
	"easy_match_search" | "social_match_search"
>

export type AccessDenial = {
	code: AccessDeniedError["code"]
	permission: UserPermissionKey | SearchPermissionKey | null
}

type PermissionCheckResult =
	| { outcome: "allowed" }
	| { outcome: "denied"; denial: AccessDenial }
	| { outcome: "session_lost" }

// Also returned when the check cannot reach the server: the server decides then.
const ALLOWED: PermissionCheckResult = { outcome: "allowed" }

function resultOf(
	access: User["permissions"][UserPermissionKey],
	permission: UserPermissionKey,
): PermissionCheckResult {
	if (access.allowed) return ALLOWED
	return { outcome: "denied", denial: { code: access.reason, permission } }
}

export async function checkPermission({
	queryClient,
	user,
	permission,
	fresh = false,
}: {
	queryClient: QueryClient
	user: User
	permission: UserPermissionKey
	// A cached allow can be stale: the grace window expires on the server alone.
	// fresh trusts only a user the server sent within USER_STALE_MS.
	fresh?: boolean
}): Promise<PermissionCheckResult> {
	if (!fresh && user.permissions[permission].allowed) return ALLOWED
	const confirmed = fresh && confirmedUserWithin(user.id, USER_STALE_MS)
	if (confirmed) return resultOf(confirmed.permissions[permission], permission)

	try {
		const serverUser = await queryClient.query({
			...userQueryOptions(user.id),
			staleTime: 0,
		})
		return resultOf(serverUser.permissions[permission], permission)
	} catch (error) {
		if (isTransientFailure(error)) return ALLOWED
		captureError(error, { scope: "permissionCheck.refresh" })
		return { outcome: "session_lost" }
	}
}
